import { AnimatedCounter } from "./AnimatedCounter";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

const industryData = [
  {
    industry: "E-commerce",
    openRate: 15.7,
    clickRate: 2.1,
    unsubRate: 0.21,
    q4Change: -18
  },
  {
    industry: "SaaS / Technology",
    openRate: 22.4,
    clickRate: 2.8,
    unsubRate: 0.18,
    q4Change: -12
  },
  {
    industry: "Financial Services",
    openRate: 27.1,
    clickRate: 3.4,
    unsubRate: 0.12,
    q4Change: -8
  },
  {
    industry: "Healthcare",
    openRate: 23.5,
    clickRate: 3.0,
    unsubRate: 0.14,
    q4Change: -6
  },
  {
    industry: "Media & Publishing",
    openRate: 20.8,
    clickRate: 4.1,
    unsubRate: 0.10,
    q4Change: -15
  },
  {
    industry: "Travel & Hospitality",
    openRate: 18.2,
    clickRate: 2.3,
    unsubRate: 0.24,
    q4Change: -22
  },
  {
    industry: "Retail",
    openRate: 17.1,
    clickRate: 2.4,
    unsubRate: 0.19,
    q4Change: -20
  },
  {
    industry: "B2B Services",
    openRate: 21.6,
    clickRate: 2.9,
    unsubRate: 0.15,
    q4Change: -9
  }
];

const metricTooltips = {
  openRate: {
    label: "Open %",
    description: "Percentage of delivered emails that were opened. Affected by Apple Mail Privacy Protection which can inflate rates."
  },
  clickRate: {
    label: "Click %",
    description: "Percentage of delivered emails where recipients clicked at least one link. Key indicator of content relevance."
  },
  unsubRate: {
    label: "Unsub %",
    description: "Percentage of recipients who unsubscribed after receiving the email. Lower is better; above 0.5% signals issues."
  },
  q4Change: {
    label: "Q4 Δ",
    description: "Typical change in engagement during Q4 (Oct-Dec) vs. yearly average. Negative values indicate decreased performance."
  }
};

interface MetricHeaderProps {
  metricKey: keyof typeof metricTooltips;
}

const MetricHeader = ({ metricKey }: MetricHeaderProps) => {
  const metric = metricTooltips[metricKey];
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="inline-flex items-center gap-1 cursor-help">
          {metric.label}
          <Info className="w-3 h-3 text-muted-foreground/60" />
        </span>
      </TooltipTrigger>
      <TooltipContent side="top" className="max-w-[200px] text-xs">
        <p>{metric.description}</p>
      </TooltipContent>
    </Tooltip>
  );
};

export const IndustryBenchmarksSection = () => {
  return (
    <TooltipProvider delayDuration={200}>
      <section className="section-card mt-5 sm:mt-7">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-baseline gap-1.5 sm:gap-2 mb-5 sm:mb-6">
          <h2 className="font-display text-lg sm:text-xl font-semibold tracking-tight text-foreground">
            Industry email benchmarks
          </h2>
          <p className="text-[11px] sm:text-xs text-muted-foreground">
            Average performance metrics by sector (2024 data)
          </p>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-4 text-[10px] sm:text-xs text-muted-foreground">
          <span className="font-medium text-foreground">Metrics:</span>
          <span>Hover headers for details</span>
        </div>

        {/* Table */}
        <div className="overflow-x-auto -mx-3 sm:-mx-4 px-3 sm:px-4">
          <table className="w-full min-w-[500px]">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left text-[10px] sm:text-xs font-medium text-muted-foreground py-2 pr-4">Industry</th>
                <th className="text-right text-[10px] sm:text-xs font-medium text-muted-foreground py-2 px-2">
                  <MetricHeader metricKey="openRate" />
                </th>
                <th className="text-right text-[10px] sm:text-xs font-medium text-muted-foreground py-2 px-2">
                  <MetricHeader metricKey="clickRate" />
                </th>
                <th className="text-right text-[10px] sm:text-xs font-medium text-muted-foreground py-2 px-2">
                  <MetricHeader metricKey="unsubRate" />
                </th>
                <th className="text-right text-[10px] sm:text-xs font-medium text-muted-foreground py-2 pl-2">
                  <MetricHeader metricKey="q4Change" />
                </th>
              </tr>
            </thead>
            <tbody>
              {industryData.map((row, i) => (
                <tr 
                  key={i} 
                  className="border-b border-border/50 last:border-0 hover:bg-muted/30 transition-colors"
                >
                  <td className="text-xs sm:text-sm font-medium text-foreground py-2.5 pr-4">
                    {row.industry}
                  </td>
                  <td className="text-right text-xs tabular-nums text-foreground py-2.5 px-2">
                    <AnimatedCounter value={row.openRate} suffix="%" duration={1000 + i * 50} />
                  </td>
                  <td className="text-right text-xs tabular-nums text-foreground py-2.5 px-2">
                    <AnimatedCounter value={row.clickRate} suffix="%" duration={1100 + i * 50} />
                  </td>
                  <td className="text-right text-xs tabular-nums text-muted-foreground py-2.5 px-2">
                    <AnimatedCounter value={row.unsubRate} suffix="%" decimals={2} duration={1200 + i * 50} />
                  </td>
                  <td className="text-right text-xs tabular-nums font-medium text-destructive py-2.5 pl-2">
                    {row.q4Change}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Insights */}
        <div className="grid sm:grid-cols-3 gap-3 mt-5 pt-4 border-t border-border">
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-lg sm:text-xl font-semibold text-foreground mb-1">
              <AnimatedCounter value={20.9} suffix="%" duration={1400} />
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Average open rate across all industries</p>
          </div>
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-lg sm:text-xl font-semibold text-foreground mb-1">
              <AnimatedCounter value={2.9} suffix="%" duration={1500} />
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Average click-through rate</p>
          </div>
          <div className="bg-muted/30 rounded-lg p-3">
            <div className="text-lg sm:text-xl font-semibold text-destructive mb-1">
              -13.8%
            </div>
            <p className="text-[10px] sm:text-xs text-muted-foreground">Average Q4 engagement drop</p>
          </div>
        </div>

        <p className="text-[10px] sm:text-[11px] text-muted-foreground mt-4">
          Data compiled from GetResponse and Mailchimp 2024 benchmark reports. 
          Q4 change reflects typical December performance vs. yearly average.
        </p>
      </section>
    </TooltipProvider>
  );
};
